import React, { useState, useEffect, useMemo, useCallback } from "react";

// Import services
import {
  getTrainingData,
  getAllRegisteredTrainee,
  getTrainingCategories,
  getTrainingPrinciples,
  fetchFrequency,
  getSource,
  getTrainingDashboardAnalytics
} from "./services/trainingService";
import { TrainingIcon } from "./components/TrainingIcon";
import "./TrainingDashboard.css";
import { useFinancialYear } from "../../../context/FinancialYearContext";

// Import components
import FilterSection from "./components/filters/FilterSection";
import { EmployeesStatsCards, StatsCards, TrainingCoverageSummary } from "./components/training/TrainingSummaryComponents";
import { DetailedTrainingCoverageBreakdown } from "./components/training/DetailedBreakdownComponent";
import { PrincipleCoverageAnalysis } from "./components/training/PrincipleAnalysisComponent";
import PrincipleComplianceMatrix from "./components/training/PrincipleComplianceMatrix";
import { PrincipleCoverageBySegment } from "./components/training/PrincipleCoverageBySegment";
import { HumanRightsTrainingCoverageDetails } from "./components/training/HumanRightsTrainingCoverageDetails";

// Import hooks
import { useTrainingComplianceData } from "./hooks/useComplianceData";
import { generateTimePeriodOptions, getStartingMonth } from "../../CarbonFootPrinting/utils/PeriodCalculationUtils";
import LocationWiseGraph from "./LocationWiseGraph";
import GenderChart from "./GenderDistributionGraph";
import YearlyTrainingGraph from "./YearlyTrainingGraph";
import HumanRightsGraph from "./HumanRightsGraph";
import TrainingCoverageCharts from "./TrainingCoverageCharts";
import TrainingCoverage from "../training-coverage/TrainingCoverage";

const TrainingDashboard = () => {
  // State variables
  const [activeTab, setActiveTab] = useState("trainings");
  const [loading, setLoading] = useState(false);
  /** True until the first data load finishes; gates the full-page spinner so later year changes refresh in the background instead of blocking. */
  const [initialized, setInitialized] = useState(false);
  const [error, setError] = useState(null);
  const [visualizationType, setVisualizationType] = useState("analysis");

  // Financial Year state — primary FY comes from the global filter (all tabs).
  const { selectedFYId: financialYearId, setSelectedFYId, financialYears: financialYear } = useFinancialYear();
  const [financialYearValue, setFinancialYearValue] = useState(null);
  const [previousFinancialYearId, setPreviousFinancialYearId] = useState([]);
  const [previousFinancialYearValue, setPreviousFinancialYearValue] = useState(null);

  // Training data state
  const [trainingData, setTrainingData] = useState([]);
  const [previousTrainingData, setPreviousTrainingData] = useState([]);
  const [trainingCategories, setTrainingCategories] = useState([]);
  const [trainingPrinciples, setTrainingPrinciples] = useState([]);

  // Add trainee list state
  const [traineeList, setTraineeList] = useState([]);
  const [previousTraineeList, setPreviousTraineeList] = useState([]);

  /** Stat-card metrics computed server-side (training-dashboard analytics endpoint). */
  const [analytics, setAnalytics] = useState(null);
  /** Previous financial year's analytics — used for the Human Rights year-over-year totals. */
  const [previousAnalytics, setPreviousAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError, setAnalyticsError] = useState(false);

  // Filter states
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedPrinciples, setSelectedPrinciples] = useState([]);
  const [selectedPeriods, setSelectedPeriods] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);

  // Other state variables
  const [frequency, setFrequency] = useState(null);
  const [locations, setLocations] = useState([]);
  const [timePeriodOptions, setTimePeriodOptions] = useState([]);

  const isDateInRange = (dateToCheck, rangeLabel, startMonthIndex, financialYear) => {
    const [startYearStr, endYearStr] = financialYear.split('-');
    const startYear = parseInt(startYearStr);

    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    const [labelStartStr, labelEndStr] = rangeLabel.split(' - ').map(m => m.trim());
    const labelStartIndex = monthNames.indexOf(labelStartStr);
    const labelEndIndex = monthNames.indexOf(labelEndStr);

    if (labelStartIndex === -1 || labelEndIndex === -1) {
      throw new Error("Invalid range label. Use short month names like 'Apr', 'Jun'.");
    }

    function getActualYear(monthIndex) {
      return (monthIndex >= startMonthIndex) ? startYear : startYear + 1;
    }

    const startDate = new Date(getActualYear(labelStartIndex), labelStartIndex, 1);
    const endDate = new Date(getActualYear(labelEndIndex), labelEndIndex + 1, 0);  // 0 = last day of previous month

    return dateToCheck >= startDate && dateToCheck <= endDate;
  }


  // Enhanced filtering logic using useMemo for performance
  const filterTrainings = (data, {selectedCategories, selectedLocations, selectedPeriods, timePeriodOptions }) => {
    if (!data || data.length === 0) return [];

    return data.filter(training => {
      // Filter by categories
      // if (selectedCategories.length > 0) {
      //   const trainingCategoryIds = (training.categories || []).map(category => category?.id).filter(Boolean);
      //   const categoryMatch = selectedCategories.some(categoryId =>
      //     trainingCategoryIds.includes(categoryId)
      //   );
      //   if (!categoryMatch) return false;
      // }

      // Filter by locations
      if (selectedLocations.length > 0) {
        const locationMatch = selectedLocations.some(locationId =>
          training.locationId === locationId
        );
        if (!locationMatch) return false;
      }

      // Filter by periods
      // if (selectedPeriods.length > 0) {
      //   const trainingDate = new Date(training.fromDate || training.toDate);
      //   const periodMatch = selectedPeriods.some(periodValue => {
      //     // Find the matching period option
      //     const periodOption = timePeriodOptions.find(option => option.value === periodValue);
      //     const finanacialValue = financialYear.find(option => option.id === financialYearId);
      //     const start = JSON.parse(localStorage.getItem("currentUser"))?.starting_month || null;
      //     const result = isDateInRange(trainingDate, periodOption.label, start, finanacialValue.financial_year_value);
      //     return result;
      //   });
      //   if (!periodMatch) return false;
      // }

      return true;
    });
  };

  const filterArgs = {
    selectedCategories,
    selectedLocations,
    selectedPeriods,
    timePeriodOptions,
  };

  const filteredTrainingData = useMemo(
    () => filterTrainings(trainingData, filterArgs),
    [trainingData, filterArgs]
  );

  const filteredPreviousTrainingData = useMemo(
    () => filterTrainings(previousTrainingData, filterArgs),
    [previousTrainingData, filterArgs]
  );

  // Use custom hooks with filtered data

  const complianceData = useTrainingComplianceData(filteredTrainingData, traineeList, analytics?.categoryCoverage);


  // Initialize data on component mount
  useEffect(() => {
    const initializeData = async () => {
      try {
        setLoading(true);
        // FY-independent lookups; the financial year itself is global.
        const [categories, principles, locations] = await Promise.all([
          getTrainingCategories(),
          getTrainingPrinciples(),
          getSource()
        ]);

        setTrainingCategories(categories);
        setTrainingPrinciples(principles);
        setLocations(locations);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    initializeData();
  }, []);

  // Load training data when financial year changes
  useEffect(() => {
    if (financialYearId) {
      const loadTrainingData = async () => {
        try {
          setLoading(true);
          const currentFYIndex = financialYear.findIndex(item => item.id === financialYearId);
          const previousFY = currentFYIndex > 0 ? financialYear[currentFYIndex - 1] : null;

          setFinancialYearValue(financialYear[currentFYIndex]?.financial_year_value);
          setPreviousFinancialYearId(previousFY?.id);
          setPreviousFinancialYearValue(previousFY?.financial_year_value);

          const [
            trainingResult,
            trainees,
            frequencyData,
            previousTrainingResult,
            previousTrainees
          ] = await Promise.all([
            getTrainingData(financialYearId, financialYear),
            getAllRegisteredTrainee(financialYearId, financialYear),
            fetchFrequency(financialYearId),

            previousFY?.id
              ? getTrainingData(previousFY.id, financialYear)
              : Promise.resolve([]),

            previousFY?.id
              ? getAllRegisteredTrainee(previousFY.id, financialYear)
              : Promise.resolve([]),
          ]);

          setTraineeList(trainees);
          setTrainingData(trainingResult);
          setFrequency(frequencyData);
          setPreviousTrainingData(previousTrainingResult);
          setPreviousTraineeList(previousTrainees);
        } catch (err) {
          setError(err.message);
        } finally {
          setLoading(false);
          setInitialized(true);
        }
      };

      loadTrainingData();
    }
  }, [financialYearId]);

  /** Load server-computed stat-card metrics; scoped by FY + selected locations only. */
  const loadAnalytics = useCallback(() => {
    if (!financialYearId) return undefined;
    let active = true;
    setAnalyticsLoading(true);
    setAnalyticsError(false);

    const currentFYIndex = financialYear.findIndex((item) => item.id === financialYearId);
    const previousFY = currentFYIndex > 0 ? financialYear[currentFYIndex - 1] : null;

    Promise.all([
      getTrainingDashboardAnalytics(financialYearId, selectedLocations),
      previousFY?.id
        ? getTrainingDashboardAnalytics(previousFY.id, selectedLocations)
        : Promise.resolve(null)
    ])
      .then(([current, previous]) => {
        if (!active) return;
        if (current) {
          setAnalytics(current);
          setPreviousAnalytics(previous);
        } else {
          setAnalyticsError(true);
        }
      })
      .catch(() => {
        if (active) setAnalyticsError(true);
      })
      .finally(() => {
        if (active) setAnalyticsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [financialYearId, selectedLocations, financialYear]);

  useEffect(() => loadAnalytics(), [loadAnalytics]);

  useEffect(() => {
    if (frequency) {
      const start = getStartingMonth();
      const options = generateTimePeriodOptions(frequency, start);
      setTimePeriodOptions(options);
    }
  }, [frequency]);

  // Prepare filter options
  const categoryOptions = trainingCategories.map(cat => ({
    value: cat.id || cat?.title?.toLowerCase(),
    label: cat.title
  }));

  const principleOptions = trainingPrinciples.map(principle => ({
    value: principle.id,
    label: principle.title
  }));

  const locationOptions = locations.map(loc => ({
    value: loc.id,
    label: loc?.unitCode || `${loc?.location?.area || ""}, ${loc?.location?.city || ""}`.trim()
  }));

  /** Human-readable label for the location scope shown in the coverage summary. */
  const locationFocusLabel = selectedLocations.length === 0
    ? 'All'
    : locationOptions
        .filter(opt => selectedLocations.includes(opt.value))
        .map(opt => opt.label)
        .join(', ') || 'All';

  const periodOptions = (timePeriodOptions || []).map(period => ({
    value: period.value,
    label: period.label
  }));

  // Event handlers — updates the global financial year (drives every tab).
  const handleFinancialYearChange = (e) => {
    setSelectedFYId(parseInt(e.target.value));
  };

  const handleClearFilters = () => {
    setSelectedCategories([]);
    setSelectedPeriods([]);
    setSelectedLocations([]);
  };

  if (loading && !initialized) {
    return (
      <div className="trd-root">
        <div className="trd-state">
          <div className="trd-state-box">
            <div className="trd-spinner" />
            <div className="trd-state-title">Loading training data…</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="trd-root">
        <div className="trd-state">
          <div className="trd-state-box trd-state-box--error">
            <div className="trd-state-icon"><TrainingIcon name="alert" size={26} /></div>
            <div className="trd-state-title trd-state-title--error">Error Loading Data</div>
            <div className="trd-state-msg">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="trd-root">
      <div className="trd-container">

        <div className="trd-page-head">
          <span className="trd-page-badge"><TrainingIcon name="award" size={22} /></span>
          <div>
            <h1 className="trd-page-title">Training Dashboard</h1>
            <p className="trd-page-subtitle">Coverage and compliance across employees and workers</p>
          </div>
        </div>

        <FilterSection
          financialYear={financialYear}
          financialYearId={financialYearId}
          onFinancialYearChange={handleFinancialYearChange}
          selectedCategories={selectedCategories}
          setSelectedCategories={setSelectedCategories}
          categoryOptions={categoryOptions}
          selectedPeriods={selectedPeriods}
          setSelectedPeriods={setSelectedPeriods}
          periodOptions={periodOptions}
          selectedLocations={selectedLocations}
          setSelectedLocations={setSelectedLocations}
          locationOptions={locationOptions}
          onClearFilters={handleClearFilters}
          activeTab={activeTab}
          visualizationType={visualizationType}
        />

        <div className="trd-toolbar">
          <div className="trd-view-toggle" role="tablist" aria-label="Visualization type">
            <button
              type="button"
              role="tab"
              aria-selected={visualizationType === "analysis"}
              className={`trd-seg${visualizationType === "analysis" ? " trd-seg--active" : ""}`}
              onClick={() => setVisualizationType("analysis")}
            >
              <TrainingIcon name="grid" size={15} />
              Analysis
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={visualizationType === "graph"}
              className={`trd-seg${visualizationType === "graph" ? " trd-seg--active" : ""}`}
              onClick={() => setVisualizationType("graph")}
            >
              <TrainingIcon name="bars" size={15} />
              Graph
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={visualizationType === "coverage"}
              className={`trd-seg${visualizationType === "coverage" ? " trd-seg--active" : ""}`}
              onClick={() => setVisualizationType("coverage")}
            >
              <TrainingIcon name="grid" size={15} />
              Coverage
            </button>
          </div>
        </div>

        {visualizationType === 'analysis' && (
          <>
            {analyticsError && !analytics ? (
              <div className="trd-stat-error">
                <TrainingIcon name="alert" size={18} />
                <span>Couldn't load the stat cards for this selection. Please try again.</span>
                <button type="button" onClick={loadAnalytics}>Retry</button>
              </div>
            ) : (
              <>
                <StatsCards analytics={analytics} loading={analyticsLoading} />

                <EmployeesStatsCards analytics={analytics} loading={analyticsLoading} />
              </>
            )}

            <PrincipleComplianceMatrix mockData={complianceData} />

            <PrincipleCoverageAnalysis complianceData={complianceData} />
          

          <PrincipleCoverageBySegment
            trainingData={filteredTrainingData}
            categoryCoverage={analytics?.categoryCoverage}
          />

            <DetailedTrainingCoverageBreakdown
              trainingData={filteredTrainingData}
              traineeList={traineeList}
              financialYear={financialYear}
              financialYearId={financialYearId}
              categoryCoverage={analytics?.categoryCoverage}
              categoryIds={analytics?.categoryIds}
            />

            <HumanRightsTrainingCoverageDetails
              trainingData={filteredTrainingData}
              previousTrainingData={filteredPreviousTrainingData}
              traineeList={traineeList}
              previousTraineeList={previousTraineeList}
              currentFYLabel={financialYearValue}
              previousFYLabel={previousFinancialYearValue}
              categoryCoverage={analytics?.categoryCoverage}
              previousCategoryCoverage={previousAnalytics?.categoryCoverage}
              categoryIds={analytics?.categoryIds}
            />

            <TrainingCoverageSummary
              trainingData={filteredTrainingData}
              traineeList={traineeList}
              financialYear={financialYear}
              financialYearId={financialYearId}
              categoryCoverage={analytics?.categoryCoverage}
              categoryIds={analytics?.categoryIds}
              locationFocusLabel={locationFocusLabel}
            />
          </>
        )}

        {visualizationType === 'graph' && (
          <>
            <div className="trd-chart-card">
              <TrainingCoverageCharts
                trainingData={trainingData}
                traineeList={traineeList}
                categoryOptions={categoryOptions}
                principleOptions={principleOptions}
              />
            </div>

            <div className="trd-chart-card">
              <LocationWiseGraph source={filteredTrainingData} locations={locations} />
            </div>

            <div className="trd-chart-card">
              <GenderChart sourceData={filteredTrainingData} locations={locations} />
            </div>

            <div className="trd-chart-card">
              <YearlyTrainingGraph trainingData={filteredTrainingData} trainingDataForPreviousYear={previousTrainingData} traineeList={traineeList} financialYear={financialYear} />
            </div>

            <div className="trd-chart-card">
              <HumanRightsGraph trainingData={filteredTrainingData} trainingDataForPreviousYear={previousTrainingData} traineeList={traineeList} financialYear={financialYear} />
            </div>
          </>
        )}

        {visualizationType === 'coverage' && (
          <div className="trd-chart-card">
            <TrainingCoverage embedded financialYearId={financialYearId} />
          </div>
        )}

      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default TrainingDashboard;